import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eraser, Loader2, PenLine } from "lucide-react";

export default function SignaturePad({ open, title, employeeName, items, onClose, onConfirm }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const [signer, setSigner] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSigner(employeeName || "");
    setEmpty(true);
    const c = canvasRef.current;
    if (c) {
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    }
  }, [open, employeeName]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
    canvasRef.current.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
    setEmpty(false);
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); setEmpty(true);
  };

  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(canvasRef.current.toDataURL("image/png"), signer); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><PenLine className="h-5 w-5 text-accent" /> {title}</DialogTitle>
        </DialogHeader>
        {items?.length > 0 && (
          <ul className="text-sm bg-slate-50 border rounded-md p-3 space-y-1 max-h-28 overflow-y-auto" data-testid="signature-items">
            {items.map((it, i) => <li key={i}>• {it}</li>)}
          </ul>
        )}
        <div className="space-y-1.5">
          <Label>Nombre del firmante</Label>
          <Input value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="signer-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Firma (dibuja con el dedo o el ratón)</Label>
          <canvas ref={canvasRef} width={600} height={220} data-testid="signature-canvas"
            className="w-full h-44 rounded-md border-2 border-dashed border-slate-300 bg-white touch-none cursor-crosshair"
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} />
        </div>
        <DialogFooter className="flex-row flex-wrap gap-2 justify-end">
          <Button variant="ghost" onClick={clear} data-testid="signature-clear-button"><Eraser className="h-4 w-4 mr-2" /> Borrar</Button>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={confirm} disabled={empty || busy} data-testid="signature-confirm-button">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Firmar y generar recibo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
